import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const UNSAFE = new Set(["post", "put", "patch", "delete"]);
const USER_KEY = "aivos_user";
const TOKEN_KEY = "aivos_token"; // Bearer token — required for cross-origin (custom domain) deploys

function readCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function attachCsrf(cfg) {
  if (UNSAFE.has((cfg.method || "get").toLowerCase())) {
    const csrf = readCookie("aivos_csrf");
    if (csrf) cfg.headers["X-CSRF-Token"] = csrf;
  }
  return cfg;
}

// Attach the Bearer token (primary auth) + CSRF (cookie-auth fallback for same-origin).
function attachAuth(cfg) {
  attachCsrf(cfg);
  const tok = localStorage.getItem(TOKEN_KEY);
  if (tok) cfg.headers["Authorization"] = `Bearer ${tok}`;
  // Phase 1: v1 multi-tenant header — active org id (if any). Endpoints that don't
  // need it simply ignore it; v1 endpoints use it for default tenant context.
  if (!cfg.headers["X-Organization-Id"]) {
    const orgId = localStorage.getItem("aivos_active_org");
    if (orgId) cfg.headers["X-Organization-Id"] = orgId;
  }
  return cfg;
}

// ---- Bare client used ONLY for /auth/refresh (no recursive 401 handling) ----
const bare = axios.create({ baseURL: API, withCredentials: true });
bare.interceptors.request.use(attachCsrf);

// ---- Main client ----
export const api = axios.create({ baseURL: API, withCredentials: true });
api.interceptors.request.use(attachAuth);

let refreshPromise = null;

async function tryRefresh() {
  if (!refreshPromise) {
    refreshPromise = bare
      .post("/auth/refresh")
      .then((r) => {
        if (r?.data?.user) localStorage.setItem(USER_KEY, JSON.stringify(r.data.user));
        return true;
      })
      .catch(() => false)
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err?.config;
    const status = err?.response?.status;
    const url = original?.url || "";

    if (status === 401 && original && !original._retry && !url.includes("/auth/refresh") && !url.includes("/auth/login") && !url.includes("/auth/register")) {
      original._retry = true;
      const ok = await tryRefresh();
      if (ok) {
        // CSRF cookie was rotated by the refresh response — re-attach header
        attachCsrf(original);
        return api(original);
      }
    }

    if (status === 401) {
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(TOKEN_KEY);
      const p = window.location.pathname;
      const PUBLIC_PREFIXES = ["/login", "/register", "/about", "/privacy", "/terms", "/s/"];
      const isPublic = p === "/" || PUBLIC_PREFIXES.some((pre) => p.startsWith(pre));
      if (!isPublic) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  },
);

function storeToken(data) {
  if (data?.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);
  return data;
}

export const auth = {
  register: (payload) => api.post("/auth/register", payload).then((r) => storeToken(r.data)),
  login: (payload) => api.post("/auth/login", payload).then((r) => storeToken(r.data)),
  logout: () => api.post("/auth/logout").then((r) => r.data).finally(() => localStorage.removeItem(TOKEN_KEY)),
  logoutAll: () => api.post("/auth/logout-all").then((r) => r.data).finally(() => localStorage.removeItem(TOKEN_KEY)),
  refresh: () => tryRefresh(),
  me: () => api.get("/auth/me").then((r) => r.data),
};

// ---- v1 multi-tenant API ----
export const v1 = {
  meContext: () => api.get("/v1/me/context").then((r) => r.data),
  plans: () => api.get("/v1/plans").then((r) => r.data),
  orgs: {
    list: () => api.get("/v1/organizations").then((r) => r.data),
    create: (payload) => api.post("/v1/organizations", payload).then((r) => r.data),
    get: (id) => api.get(`/v1/organizations/${id}`).then((r) => r.data),
    update: (id, payload) => api.patch(`/v1/organizations/${id}`, payload).then((r) => r.data),
  },
  members: {
    list: (orgId) => api.get(`/v1/organizations/${orgId}/members`).then((r) => r.data),
    add: (orgId, payload) => api.post(`/v1/organizations/${orgId}/members`, payload).then((r) => r.data),
    setRole: (orgId, userId, role) =>
      api.patch(`/v1/organizations/${orgId}/members/${userId}`, { role }).then((r) => r.data),
    remove: (orgId, userId) => api.delete(`/v1/organizations/${orgId}/members/${userId}`).then((r) => r.data),
  },
  projects: {
    list: (orgId) => api.get(`/v1/organizations/${orgId}/projects`).then((r) => r.data),
    create: (orgId, payload) => api.post(`/v1/organizations/${orgId}/projects`, payload).then((r) => r.data),
    get: (id) => api.get(`/v1/projects/${id}`).then((r) => r.data),
    update: (id, payload) => api.patch(`/v1/projects/${id}`, payload).then((r) => r.data),
    remove: (id) => api.delete(`/v1/projects/${id}`).then((r) => r.data),
  },
};
